/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    Button,
    Stepper,
    Step,
    StepLabel,
    AppBar,
    Toolbar,
    IconButton,
    Chip,
    Divider,
    Grid,
    TextField,
    Avatar,
    Alert,
    CircularProgress,
    Skeleton,
    Paper,
    LinearProgress,
    Fade,
    Radio,
    RadioGroup,
    FormControlLabel,
    Checkbox,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Snackbar,
} from '@mui/material';
import {
    ArrowBack,
    Flight,
    Hotel,
    DirectionsCar,
    Attractions,
    CheckCircle,
    LocationOn,
    CalendarToday,
    CreditCard,
    Lock,
    Celebration,
    ArrowForward,
    ArrowBackIos,
    Info,
    Shield,
    EventNote,
    Refresh,
    Check,
    Person,
    Add,
    Group,
    People,
} from '@mui/icons-material';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { api, isAuthenticated } from '../utils/api';
import TravelerDetailsStep, { TravelerInfo } from '../components/booking/TravelerDetailsStep';
import HotelSearchDialog from '../components/search/HotelSearchDialog';
import CarSearchDialog from '../components/search/CarSearchDialog';
import { formatDate, daysBetween } from '../utils/dateHelpers';

// ─── Types ───────────────────────────────────────────

interface Trip {
    tripId: string;
    title: string;
    destination: string;
    startDate?: string;
    endDate?: string;
    description?: string;
    status: string;
    visibility: string;
    // Traveler count fields
    travelers?: number;
    travelerCount?: number;
    adults?: number;
    children?: number;
    infants?: number;
    // Group trip fields
    is_group?: boolean;
    isGroup?: boolean;
    group_name?: string;
    groupName?: string;
    cost_sharing_model?: string;
    departing_from?: string;
    departingFrom?: string;
}

interface TripItem {
    item_id: string;
    trip_id: string;
    item_type: 'flight' | 'hotel' | 'car' | 'activity';
    title: string;
    location?: string;
    start_date?: string;
    end_date?: string;
    start_time?: string;
    end_time?: string;
    estimated_cost?: number;
    currency?: string;
    notes?: string;
    booking_url?: string;
    preferences?: Record<string, any>;
    itinerary_day?: number;
    booking_id?: string;
    item_order?: number;
}

interface UserProfile {
    userId?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    dateOfBirth?: string;
    gender?: string;
    passportNumber?: string;
    passportExpiry?: string;
    passportCountry?: string;
    knownTravelerNumber?: string;
}

// ─── Steps (4 Steps for Individual, 5 for Group) ─────

const INDIVIDUAL_STEPS = [
    { label: 'Review & Select', icon: <EventNote /> },
    { label: 'Traveler Details', icon: <Person /> },
    { label: 'Payment', icon: <CreditCard /> },
    { label: 'Confirmation', icon: <Celebration /> },
];

const GROUP_STEPS = [
    { label: 'Review & Select', icon: <EventNote /> },
    { label: 'Traveler Details', icon: <People /> },
    { label: 'Cost Split', icon: <Group /> },
    { label: 'Payment', icon: <CreditCard /> },
    { label: 'Confirmation', icon: <Celebration /> },
];

// ─── Helpers ─────────────────────────────────────────

const getItemIcon = (type: string) => {
    switch (type) {
        case 'flight': return <Flight />;
        case 'hotel': return <Hotel />;
        case 'car': return <DirectionsCar />;
        case 'activity': return <Attractions />;
        default: return <EventNote />;
    }
};

const getItemColor = (type: string) => {
    switch (type) {
        case 'flight': return '#2196F3';
        case 'hotel': return '#4A6FA5';
        case 'car': return '#607D8B';
        case 'activity': return '#2D6E6A';
        default: return '#666';
    }
};

// ─── Main Component ──────────────────────────────────

const BookingCheckoutPage: React.FC = () => {
    const navigate = useNavigate();
    const { tripId } = useParams<{ tripId: string }>();

    const [activeStep, setActiveStep] = useState(0);
    const [trip, setTrip] = useState<Trip | null>(null);
    const [tripItems, setTripItems] = useState<TripItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // User profile for pre-filling traveler details
    const [userProfile, setUserProfile] = useState<UserProfile | null>(null);

    // Selection state
    const [selectedFlightId, setSelectedFlightId] = useState<string | null>(null);
    const [selectedHotelId, setSelectedHotelId] = useState<string | null>(null);
    const [selectedCarId, setSelectedCarId] = useState<string | null>(null);
    const [selectedActivityIds, setSelectedActivityIds] = useState<string[]>([]);

    // Traveler details state
    const [travelers, setTravelers] = useState<TravelerInfo[]>([]);

    // Price refresh state
    const [expandedOrderItem, setExpandedOrderItem] = useState<string | null>(null);
    const [refreshingPrices, setRefreshingPrices] = useState(false);
    const [priceChanges, setPriceChanges] = useState<Record<string, { oldPrice: number; newPrice: number; available: boolean }>>({});
    const [showPriceChangeDialog, setShowPriceChangeDialog] = useState(false);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({ open: false, message: '' });

    // Payment state
    const [paymentProcessing, setPaymentProcessing] = useState(false);
    const [confirmationCode, setConfirmationCode] = useState('');

    // Saving state
    const [savingSelections, setSavingSelections] = useState(false);

    // Add hotel/car prompt dialogs
    const [showAddHotelPrompt, setShowAddHotelPrompt] = useState(false);
    const [showAddCarPrompt, setShowAddCarPrompt] = useState(false);
    const [hotelSearchOpen, setHotelSearchOpen] = useState(false);
    const [carSearchOpen, setCarSearchOpen] = useState(false);

    // ─── Computed: Is this a group trip? ─────────────
    const isGroupTrip = trip?.is_group || trip?.isGroup || false;
    const STEPS = isGroupTrip ? GROUP_STEPS : INDIVIDUAL_STEPS;

    // ─── Computed: Get traveler count from trip ──────
    const getTravelerCount = (): number => {
        if (!trip) return 1;
        
        // Check various field names the API might use (camelCase and snake_case)
        if (trip.travelers && trip.travelers > 0) return trip.travelers;
        if ((trip as any).traveler_count && (trip as any).traveler_count > 0) return (trip as any).traveler_count;
        if (trip.travelerCount && trip.travelerCount > 0) return trip.travelerCount;
        
        // Calculate from adults + children + infants
        const adults = trip.adults || 1;
        const children = trip.children || 0;
        const infants = trip.infants || 0;
        const total = adults + children + infants;
        
        return total > 0 ? total : 1;
    };

    // ─── Fetch Data ──────────────────────────────────

    const fetchTrip = useCallback(async () => {
        try {
            const response = await api.get(`/trips/${tripId}`);
            const data = await response.json();
            if (response.ok && data.success) {
                setTrip(data.trip);
                console.log('Trip data loaded:', data.trip);
                console.log('Traveler count from trip:', data.trip.travelers || data.trip.travelerCount || 'not set');
            } else {
                setError('Trip not found');
            }
        } catch (err) {
            setError('Failed to load trip');
        }
    }, [tripId]);

    const fetchItems = useCallback(async () => {
        try {
            const response = await api.get(`/trips/${tripId}/items`);
            const data = await response.json();
            if (response.ok && data.success) {
                const items = data.items || [];
                setTripItems(items);

                // Auto-select if only one option per category
                const flights = items.filter((i: TripItem) => i.item_type === 'flight');
                const hotels = items.filter((i: TripItem) => i.item_type === 'hotel');
                const cars = items.filter((i: TripItem) => i.item_type === 'car');
                const activities = items.filter((i: TripItem) => i.item_type === 'activity');

                if (flights.length === 1) setSelectedFlightId(flights[0].item_id);
                if (hotels.length === 1) setSelectedHotelId(hotels[0].item_id);
                if (cars.length === 1) setSelectedCarId(cars[0].item_id);
                // Auto-select all activities by default
                setSelectedActivityIds(activities.map((a: TripItem) => a.item_id));
            }
        } catch (err) {
            // Items API might fail gracefully
            console.log('Failed to fetch items:', err);
        }
    }, [tripId]);

    const fetchUserProfile = useCallback(async () => {
        try {
            // Try API first
            const response = await api.get('/users/profile');
            const data = await response.json();
            let profile: UserProfile = {};
            
            if (response.ok && data.success) {
                profile = data.profile || data.user || {};
            }
            
            // Also check localStorage for settings (legal name fields)
            const savedSettings = localStorage.getItem('userSettings');
            if (savedSettings) {
                try {
                    const settings = JSON.parse(savedSettings);
                    // Merge legal name fields from settings (these take priority for booking)
                    profile = {
                        ...profile,
                        email: profile.email || settings.email,
                        phone: profile.phone || settings.phone,
                        // Use legal name fields for booking (as on ID/passport)
                        firstName: settings.legalFirstName || profile.firstName,
                        lastName: settings.legalLastName || profile.lastName,
                        dateOfBirth: settings.dateOfBirth || profile.dateOfBirth,
                        gender: settings.gender === 'M' ? 'male' : settings.gender === 'F' ? 'female' : settings.gender === 'X' ? 'other' : profile.gender,
                        passportNumber: settings.passportNumber || profile.passportNumber,
                        passportExpiry: settings.passportExpiry || profile.passportExpiry,
                        passportCountry: settings.passportCountry || profile.passportCountry,
                        knownTravelerNumber: settings.knownTravelerNumber || settings.tsaPrecheck || profile.knownTravelerNumber,
                    };
                    console.log('Merged profile with localStorage settings:', profile);
                } catch (e) {
                    console.log('Error parsing localStorage settings:', e);
                }
            }
            
            if (Object.keys(profile).length > 0) {
                setUserProfile(profile);
                console.log('User profile loaded:', profile);
            }
        } catch (err) {
            // Try localStorage only as fallback
            const savedSettings = localStorage.getItem('userSettings');
            if (savedSettings) {
                try {
                    const settings = JSON.parse(savedSettings);
                    const profile: UserProfile = {
                        email: settings.email,
                        phone: settings.phone,
                        firstName: settings.legalFirstName,
                        lastName: settings.legalLastName,
                        dateOfBirth: settings.dateOfBirth,
                        gender: settings.gender === 'M' ? 'male' : settings.gender === 'F' ? 'female' : settings.gender === 'X' ? 'other' : '',
                        passportNumber: settings.passportNumber,
                        passportExpiry: settings.passportExpiry,
                        passportCountry: settings.passportCountry,
                        knownTravelerNumber: settings.knownTravelerNumber || settings.tsaPrecheck,
                    };
                    setUserProfile(profile);
                    console.log('User profile loaded from localStorage:', profile);
                } catch (e) {
                    console.log('User profile API not available and localStorage parse failed');
                }
            } else {
                console.log('User profile API not available - will use empty form');
            }
        }
    }, []);

    useEffect(() => {
        if (!isAuthenticated()) {
            navigate('/login');
            return;
        }
        Promise.all([fetchTrip(), fetchItems(), fetchUserProfile()]).finally(() => setLoading(false));
    }, [fetchTrip, fetchItems, fetchUserProfile, navigate]);

    // ─── Prompt for hotel/car after selections ───────

    // After flight selection, prompt for hotel if none exist
    useEffect(() => {
        if (selectedFlightId && hotelItems.length === 0 && !showAddHotelPrompt && activeStep === 0) {
            // Small delay so user sees their flight selection first
            const timer = setTimeout(() => setShowAddHotelPrompt(true), 500);
            return () => clearTimeout(timer);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFlightId]);

    // After hotel selection, prompt for car if none exist
    useEffect(() => {
        if (selectedHotelId && carItems.length === 0 && !showAddCarPrompt && activeStep === 0 && !showAddHotelPrompt) {
            const timer = setTimeout(() => setShowAddCarPrompt(true), 500);
            return () => clearTimeout(timer);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedHotelId]);

    // ─── Computed ────────────────────────────────────

    const flightItems = tripItems.filter(i => i.item_type === 'flight');
    const hotelItems = tripItems.filter(i => i.item_type === 'hotel');
    const carItems = tripItems.filter(i => i.item_type === 'car');
    const activityItems = tripItems.filter(i => i.item_type === 'activity');

    const hasFlights = flightItems.length > 0 || !!selectedFlightId;
    
    // Detect international flights based on destination
    const hasInternationalFlights = (() => {
        if (!trip?.destination) return false;
        const destination = trip.destination.toLowerCase();
        // Simple heuristic: if destination contains country name or is clearly international
        const internationalKeywords = ['international', 'abroad', 'overseas'];
        const domesticKeywords = ['usa', 'united states', 'domestic'];
        
        // Check departure vs destination
        const departingFrom = (trip.departing_from || trip.departingFrom || '').toLowerCase();
        if (departingFrom && destination) {
            // If departing from and destination are different countries, it's international
            // This is a simplified check - real implementation would use country detection
            const usStates = ['california', 'new york', 'florida', 'texas', 'illinois', 'georgia', 'nevada', 'arizona', 'colorado', 'washington'];
            const isUSDestination = usStates.some(state => destination.includes(state)) || 
                                    destination.includes('usa') || 
                                    destination.includes('united states');
            const isUSDeparture = usStates.some(state => departingFrom.includes(state)) ||
                                  departingFrom.includes('usa') ||
                                  departingFrom.includes('united states');
            
            // If one is US and other isn't, it's international
            // For now, default to false unless clearly international
        }
        
        return internationalKeywords.some(kw => destination.includes(kw));
    })();

    // Calculate total based on selections
    const selectedItems = tripItems.filter(item => {
        if (item.item_type === 'flight') return item.item_id === selectedFlightId;
        if (item.item_type === 'hotel') return item.item_id === selectedHotelId;
        if (item.item_type === 'car') return item.item_id === selectedCarId;
        if (item.item_type === 'activity') return selectedActivityIds.includes(item.item_id);
        return false;
    });

    const totalEstimated = selectedItems.reduce((sum, item) => sum + (Number(item.estimated_cost) || 0), 0);

    const tripDays = daysBetween(trip?.startDate, trip?.endDate);

    // Get traveler count for this booking
    const passengerCount = getTravelerCount();

    // Validation: Can proceed from Step 1?
    const canProceedFromStep1 = () => {
        // Must select a flight if flights exist
        if (flightItems.length > 0 && !selectedFlightId) return false;
        // Must select a hotel if hotels exist
        if (hotelItems.length > 0 && !selectedHotelId) return false;
        return true;
    };

    // Validation: Can proceed from Step 2 (Traveler Details)?
    const canProceedFromStep2 = () => {
        if (travelers.length === 0) return false;
        // Check if we have enough travelers
        if (travelers.length < passengerCount) return false;
        // Check if all travelers have required fields
        return travelers.every(t => {
            const requiredFields = ['firstName', 'lastName'];
            // Email/phone only required for primary traveler
            if (t.isPrimary) {
                requiredFields.push('email', 'phone');
            }
            if (hasFlights) {
                requiredFields.push('dateOfBirth', 'gender');
            }
            if (hasInternationalFlights) {
                requiredFields.push('passportNumber', 'passportExpiry', 'passportCountry');
            }
            return requiredFields.every(field => !!(t as any)[field]);
        });
    };

    // ─── Actions ─────────────────────────────────────

    const handleRefreshPrices = async () => {
        setRefreshingPrices(true);
        
        try {
            // Simulate checking live prices (TODO: Replace with actual API calls to Duffel/Booking.com)
            await new Promise(resolve => setTimeout(resolve, 1500));
            
            // Mock price changes - in production this would come from actual pricing APIs
            const changes: Record<string, { oldPrice: number; newPrice: number; available: boolean }> = {};
            
            selectedItems.forEach(item => {
                const oldPrice = Number(item.estimated_cost) || 0;
                // Simulate random price changes: 60% same, 30% higher, 10% unavailable
                const random = Math.random();
                if (random < 0.1) {
                    // 10% chance unavailable
                    changes[item.item_id] = { oldPrice, newPrice: 0, available: false };
                } else if (random < 0.4) {
                    // 30% chance price increased
                    const increase = Math.round(oldPrice * (0.05 + Math.random() * 0.15)); // 5-20% increase
                    changes[item.item_id] = { oldPrice, newPrice: oldPrice + increase, available: true };
                }
                // 60% stays the same - no entry needed
            });
            
            if (Object.keys(changes).length > 0) {
                setPriceChanges(changes);
                setShowPriceChangeDialog(true);
            } else {
                // All prices still valid - give option to search for better prices anyway
                setSnackbar({ open: true, message: '✓ All prices confirmed! Click on any item to search for alternatives.' });
            }
        } catch (err) {
            console.error('Price refresh failed:', err);
            setSnackbar({ open: true, message: 'Failed to refresh prices. Please try again.' });
        } finally {
            setRefreshingPrices(false);
        }
    };

    const handleFindAlternative = (itemType: string) => {
        setShowPriceChangeDialog(false);
        // Navigate to trip detail page with the specific search dialog open
        const searchParam = itemType === 'flight' ? 'flights' : itemType === 'hotel' ? 'hotels' : itemType === 'car' ? 'cars' : 'activities';
        navigate(`/trips/${tripId}?openSearch=${searchParam}`);
    };

    const handleAcceptPriceChanges = () => {
        // Update items with new prices
        const updatedItems = tripItems.map(item => {
            const change = priceChanges[item.item_id];
            if (change && change.available) {
                return { ...item, estimated_cost: change.newPrice };
            }
            return item;
        }).filter(item => {
            // Remove unavailable items
            const change = priceChanges[item.item_id];
            return !change || change.available;
        });
        
        setTripItems(updatedItems);
        setPriceChanges({});
        setShowPriceChangeDialog(false);
        setSnackbar({ open: true, message: '✓ Prices updated!' });
    };

    const handleSaveSelectionsAndProceed = async () => {
        setSavingSelections(true);

        try {
            // Delete non-selected items to clean up the trip
            const itemsToDelete = tripItems.filter(item => {
                if (item.item_type === 'flight' && item.item_id !== selectedFlightId) return true;
                if (item.item_type === 'hotel' && item.item_id !== selectedHotelId) return true;
                if (item.item_type === 'car' && carItems.length > 0 && item.item_id !== selectedCarId) return true;
                if (item.item_type === 'activity' && !selectedActivityIds.includes(item.item_id)) return true;
                return false;
            });

            // Delete unselected items
            await Promise.all(
                itemsToDelete.map(item =>
                    api.delete(`/trips/${tripId}/items/${item.item_id}`).catch(() => null)
                )
            );

            // Update trip status to 'booking'
            await api.put(`/trips/${tripId}`, { status: 'booking' }).catch(() => null);

            // Refresh items list
            await fetchItems();

            // Proceed to Traveler Details (Step 2)
            setActiveStep(1);
        } catch (err) {
            console.error('Failed to save selections:', err);
        } finally {
            setSavingSelections(false);
        }
    };

    const handleProceedToPayment = () => {
        // For group trips, go to Cost Split (Step 3), then Payment (Step 4)
        // For individual trips, go directly to Payment (Step 3)
        if (isGroupTrip) {
            setActiveStep(2); // Cost Split step
        } else {
            setActiveStep(2); // Payment step
        }
    };

    const handleProceedFromCostSplit = () => {
        // Group trips only - proceed from Cost Split to Payment
        setActiveStep(3);
    };

    const handleCompleteBooking = async () => {
        setPaymentProcessing(true);

        try {
            // TODO: Process payment via Stripe

            // Update trip status to 'booked' - send full trip data to avoid API issues
            const updateResponse = await api.put(`/trips/${tripId}`, { 
                status: 'booked',
                title: trip?.title,
                destination: trip?.destination,
                startDate: trip?.startDate,
                endDate: trip?.endDate,
            });
            
            if (updateResponse.ok) {
                // Update local trip state
                setTrip(prev => prev ? { ...prev, status: 'booked' } : prev);
            }
            
            // Mark items as booked
            await Promise.all(
                selectedItems.map(item =>
                    api.put(`/trips/${tripId}/items/${item.item_id}`, {
                        booking_status: 'booked',
                        booking_id: `BK-${Date.now().toString(36).toUpperCase()}`,
                    }).catch(err => console.log('Item update failed:', err))
                )
            );

            // Generate confirmation code
            setConfirmationCode(`PER-${Date.now().toString(36).toUpperCase()}`);
            
            // Move to confirmation step
            setActiveStep(isGroupTrip ? 4 : 3);
        } catch (err) {
            console.error('Booking failed:', err);
        } finally {
            setPaymentProcessing(false);
        }
    };

    // ─── Toggle Activity Selection ───────────────────

    const toggleActivity = (activityId: string) => {
        setSelectedActivityIds(prev =>
            prev.includes(activityId)
                ? prev.filter(id => id !== activityId)
                : [...prev, activityId]
        );
    };

    // ─── Handle adding hotels/cars from prompts ──────

    const handleHotelsSelected = async (hotels: any[]) => {
        try {
            for (const hotel of hotels) {
                await api.post(`/trips/${tripId}/items`, {
                    item_type: 'hotel',
                    title: hotel.name,
                    location: hotel.location,
                    start_date: trip?.startDate,
                    end_date: trip?.endDate,
                    estimated_cost: hotel.totalPrice,
                    notes: `${hotel.starRating}★ · ${hotel.reviewScore}/10`,
                });
            }
            await fetchItems();
        } catch (err) {
            console.error('Failed to add hotels:', err);
        }
    };

    const handleCarsSelected = async (cars: any[]) => {
        try {
            for (const car of cars) {
                await api.post(`/trips/${tripId}/items`, {
                    item_type: 'car',
                    title: `${car.carName} (${car.company})`,
                    location: car.pickupLocation,
                    start_date: trip?.startDate,
                    end_date: trip?.endDate,
                    estimated_cost: car.totalPrice,
                    notes: `${car.carType} · ${car.transmission}`,
                });
            }
            await fetchItems();
        } catch (err) {
            console.error('Failed to add cars:', err);
        }
    };

    // ─── Loading ─────────────────────────────────────

    if (loading) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: '#FAFAFA' }}>
                <AppBar position="static" color="transparent" elevation={0} sx={{ borderBottom: '1px solid #eee' }}>
                    <Toolbar>
                        <Skeleton width={200} height={32} />
                    </Toolbar>
                </AppBar>
                <Container maxWidth="lg" sx={{ py: 4 }}>
                    <Skeleton variant="rounded" height={60} sx={{ mb: 3, borderRadius: 3 }} />
                    <Skeleton variant="rounded" height={400} sx={{ borderRadius: 3 }} />
                </Container>
            </Box>
        );
    }

    if (error || !trip) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: '#FAFAFA' }}>
                <AppBar position="static" color="transparent" elevation={0} sx={{ borderBottom: '1px solid #eee' }}>
                    <Toolbar>
                        <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}><ArrowBack /></IconButton>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>Booking</Typography>
                    </Toolbar>
                </AppBar>
                <Container maxWidth="sm" sx={{ py: 6, textAlign: 'center' }}>
                    <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error || 'Trip not found'}</Alert>
                    <Button variant="contained" onClick={() => navigate('/trips')}>Back to My Trips</Button>
                </Container>
            </Box>
        );
    }

    // ═══════════════════════════════════════════════════
    // STEP 1: REVIEW & SELECT
    // ═══════════════════════════════════════════════════

    const renderStep1 = () => (
        <Fade in={activeStep === 0}>
            <Box>
                {/* Trip Summary Card */}
                <Card sx={{ borderRadius: 3, mb: 3, overflow: 'hidden' }}>
                    <Box sx={{ height: 4, bgcolor: isGroupTrip ? '#2196F3' : '#FF6B35' }} />
                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: 'Poppins, sans-serif' }}>
                                {trip.title}
                            </Typography>
                            {isGroupTrip && (
                                <Chip 
                                    icon={<Group sx={{ fontSize: 16 }} />} 
                                    label="Group Trip" 
                                    size="small" 
                                    sx={{ bgcolor: '#E3F2FD', color: '#1976D2', fontWeight: 600 }} 
                                />
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2, color: 'text.secondary' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <LocationOn sx={{ fontSize: 18 }} />
                                <Typography variant="body2">{trip.destination}</Typography>
                            </Box>
                            {trip.startDate && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <CalendarToday sx={{ fontSize: 18 }} />
                                    <Typography variant="body2">
                                        {formatDate(trip.startDate, { month: 'short', day: 'numeric' })} – {formatDate(trip.endDate, { month: 'short', day: 'numeric' })} ({tripDays} days)
                                    </Typography>
                                </Box>
                            )}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Person sx={{ fontSize: 18 }} />
                                <Typography variant="body2">
                                    {passengerCount} traveler{passengerCount !== 1 ? 's' : ''}
                                </Typography>
                            </Box>
                        </Box>
                        {trip.description && (
                            <Typography variant="body2" color="text.secondary">
                                {trip.description}
                            </Typography>
                        )}
                    </CardContent>
                </Card>

                {/* Traveler Count Info */}
                {passengerCount > 1 && (
                    <Alert severity="info" sx={{ borderRadius: 2, mb: 3 }} icon={<People />}>
                        <Typography variant="body2">
                            <strong>Booking for {passengerCount} travelers.</strong> You'll enter details for each traveler in the next step.
                        </Typography>
                    </Alert>
                )}

                {/* Refresh Prices Button */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <Button
                        variant="outlined"
                        startIcon={refreshingPrices ? <CircularProgress size={16} /> : <Refresh />}
                        onClick={handleRefreshPrices}
                        disabled={refreshingPrices}
                        sx={{
                            borderRadius: '20px', textTransform: 'none', borderColor: '#2D6E6A', color: '#2D6E6A',
                            '&:hover': { borderColor: '#009688', bgcolor: '#E8F5F3' },
                        }}
                    >
                        {refreshingPrices ? 'Checking prices...' : 'Refresh Prices'}
                    </Button>
                </Box>

                {/* ─── FLIGHTS SELECTION ─── */}
                {flightItems.length > 0 && (
                    <Card sx={{ borderRadius: 3, mb: 3 }}>
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <Avatar sx={{ bgcolor: '#E3F2FD', color: '#2196F3', width: 32, height: 32 }}>
                                    <Flight sx={{ fontSize: 18 }} />
                                </Avatar>
                                <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: 'Poppins, sans-serif' }}>
                                    Select Flight
                                </Typography>
                                {flightItems.length > 1 && (
                                    <Chip label={`${flightItems.length} options`} size="small" sx={{ ml: 'auto', bgcolor: '#E3F2FD', color: '#1976D2' }} />
                                )}
                                <Button
                                    size="small"
                                    variant="text"
                                    onClick={() => handleFindAlternative('flight')}
                                    sx={{ ml: flightItems.length > 1 ? 1 : 'auto', textTransform: 'none', color: '#2D6E6A' }}
                                >
                                    Search More
                                </Button>
                            </Box>

                            <RadioGroup value={selectedFlightId || ''} onChange={(e) => setSelectedFlightId(e.target.value)}>
                                {flightItems.map((flight) => (
                                    <Paper
                                        key={flight.item_id}
                                        sx={{
                                            p: 2, mb: 1.5, borderRadius: 2, cursor: 'pointer',
                                            border: selectedFlightId === flight.item_id ? '2px solid #2196F3' : '1px solid #e0e0e0',
                                            bgcolor: selectedFlightId === flight.item_id ? '#E3F2FD' : 'white',
                                            transition: 'all 0.2s',
                                            '&:hover': { borderColor: '#2196F3', bgcolor: '#F5F9FF' },
                                        }}
                                        onClick={() => setSelectedFlightId(flight.item_id)}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Radio value={flight.item_id} sx={{ '&.Mui-checked': { color: '#2196F3' } }} />
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="body1" sx={{ fontWeight: 600 }}>{flight.title}</Typography>
                                                {flight.location && (
                                                    <Typography variant="body2" color="text.secondary">{flight.location}</Typography>
                                                )}
                                                {flight.start_date && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {formatDate(flight.start_date)}{flight.end_date ? ` – ${formatDate(flight.end_date)}` : ''}
                                                    </Typography>
                                                )}
                                            </Box>
                                            <Box sx={{ textAlign: 'right' }}>
                                                <Typography variant="h6" sx={{ fontWeight: 700, color: '#FF6B35' }}>
                                                    ${Number(flight.estimated_cost || 0).toFixed(0)}
                                                </Typography>
                                                {passengerCount > 1 && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        per person
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </Paper>
                                ))}
                            </RadioGroup>

                            {flightItems.length > 1 && !selectedFlightId && (
                                <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
                                    Please select a flight to continue
                                </Alert>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* ─── HOTELS SELECTION ─── */}
                {hotelItems.length > 0 && (
                    <Card sx={{ borderRadius: 3, mb: 3 }}>
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <Avatar sx={{ bgcolor: '#F3E5F5', color: '#4A6FA5', width: 32, height: 32 }}>
                                    <Hotel sx={{ fontSize: 18 }} />
                                </Avatar>
                                <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: 'Poppins, sans-serif' }}>
                                    Select Hotel
                                </Typography>
                                {hotelItems.length > 1 && (
                                    <Chip label={`${hotelItems.length} options`} size="small" sx={{ ml: 'auto', bgcolor: '#F3E5F5', color: '#3D5A8A' }} />
                                )}
                                <Button
                                    size="small"
                                    variant="text"
                                    onClick={() => handleFindAlternative('hotel')}
                                    sx={{ ml: hotelItems.length > 1 ? 1 : 'auto', textTransform: 'none', color: '#2D6E6A' }}
                                >
                                    Search More
                                </Button>
                            </Box>

                            <RadioGroup value={selectedHotelId || ''} onChange={(e) => setSelectedHotelId(e.target.value)}>
                                {hotelItems.map((hotel) => (
                                    <Paper
                                        key={hotel.item_id}
                                        sx={{
                                            p: 2, mb: 1.5, borderRadius: 2, cursor: 'pointer',
                                            border: selectedHotelId === hotel.item_id ? '2px solid #4A6FA5' : '1px solid #e0e0e0',
                                            bgcolor: selectedHotelId === hotel.item_id ? '#F3E5F5' : 'white',
                                            transition: 'all 0.2s',
                                            '&:hover': { borderColor: '#4A6FA5', bgcolor: '#FDF5FF' },
                                        }}
                                        onClick={() => setSelectedHotelId(hotel.item_id)}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Radio value={hotel.item_id} sx={{ '&.Mui-checked': { color: '#4A6FA5' } }} />
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="body1" sx={{ fontWeight: 600 }}>{hotel.title}</Typography>
                                                {hotel.location && (
                                                    <Typography variant="body2" color="text.secondary">{hotel.location}</Typography>
                                                )}
                                                {hotel.start_date && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {formatDate(hotel.start_date)}{hotel.end_date ? ` – ${formatDate(hotel.end_date)}` : ''}
                                                    </Typography>
                                                )}
                                            </Box>
                                            <Box sx={{ textAlign: 'right' }}>
                                                <Typography variant="h6" sx={{ fontWeight: 700, color: '#FF6B35' }}>
                                                    ${Number(hotel.estimated_cost || 0).toFixed(0)}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    total stay
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Paper>
                                ))}
                            </RadioGroup>

                            {hotelItems.length > 1 && !selectedHotelId && (
                                <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
                                    Please select a hotel to continue
                                </Alert>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* ─── CAR RENTAL (Optional) ─── */}
                {carItems.length > 0 && (
                    <Card sx={{ borderRadius: 3, mb: 3 }}>
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <Avatar sx={{ bgcolor: '#ECEFF1', color: '#607D8B', width: 32, height: 32 }}>
                                    <DirectionsCar sx={{ fontSize: 18 }} />
                                </Avatar>
                                <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: 'Poppins, sans-serif' }}>
                                    Car Rental (Optional)
                                </Typography>
                            </Box>

                            <RadioGroup value={selectedCarId || ''} onChange={(e) => setSelectedCarId(e.target.value || null)}>
                                <FormControlLabel
                                    value=""
                                    control={<Radio sx={{ '&.Mui-checked': { color: '#607D8B' } }} />}
                                    label="No car rental needed"
                                    sx={{ mb: 1 }}
                                />
                                {carItems.map((car) => (
                                    <Paper
                                        key={car.item_id}
                                        sx={{
                                            p: 2, mb: 1.5, borderRadius: 2, cursor: 'pointer',
                                            border: selectedCarId === car.item_id ? '2px solid #607D8B' : '1px solid #e0e0e0',
                                            bgcolor: selectedCarId === car.item_id ? '#ECEFF1' : 'white',
                                            transition: 'all 0.2s',
                                            '&:hover': { borderColor: '#607D8B', bgcolor: '#F5F7F8' },
                                        }}
                                        onClick={() => setSelectedCarId(car.item_id)}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Radio value={car.item_id} sx={{ '&.Mui-checked': { color: '#607D8B' } }} />
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="body1" sx={{ fontWeight: 600 }}>{car.title}</Typography>
                                                {car.location && (
                                                    <Typography variant="body2" color="text.secondary">{car.location}</Typography>
                                                )}
                                            </Box>
                                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#FF6B35' }}>
                                                ${Number(car.estimated_cost || 0).toFixed(0)}
                                            </Typography>
                                        </Box>
                                    </Paper>
                                ))}
                            </RadioGroup>
                        </CardContent>
                    </Card>
                )}

                {/* ─── ACTIVITIES (Multi-select) ─── */}
                {activityItems.length > 0 && (
                    <Card sx={{ borderRadius: 3, mb: 3 }}>
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <Avatar sx={{ bgcolor: '#E8F5F3', color: '#2D6E6A', width: 32, height: 32 }}>
                                    <Attractions sx={{ fontSize: 18 }} />
                                </Avatar>
                                <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: 'Poppins, sans-serif' }}>
                                    Activities & Experiences
                                </Typography>
                                <Chip
                                    label={`${selectedActivityIds.length} of ${activityItems.length} selected`}
                                    size="small"
                                    sx={{ ml: 'auto', bgcolor: '#E8F5F3', color: '#00695C' }}
                                />
                            </Box>

                            {activityItems.map((activity) => (
                                <Paper
                                    key={activity.item_id}
                                    sx={{
                                        p: 2, mb: 1.5, borderRadius: 2, cursor: 'pointer',
                                        border: selectedActivityIds.includes(activity.item_id) ? '2px solid #2D6E6A' : '1px solid #e0e0e0',
                                        bgcolor: selectedActivityIds.includes(activity.item_id) ? '#E8F5F3' : 'white',
                                        transition: 'all 0.2s',
                                        '&:hover': { borderColor: '#2D6E6A', bgcolor: '#F0FAF9' },
                                    }}
                                    onClick={() => toggleActivity(activity.item_id)}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Checkbox
                                            checked={selectedActivityIds.includes(activity.item_id)}
                                            sx={{ '&.Mui-checked': { color: '#2D6E6A' } }}
                                        />
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="body1" sx={{ fontWeight: 600 }}>{activity.title}</Typography>
                                            {activity.location && (
                                                <Typography variant="body2" color="text.secondary">{activity.location}</Typography>
                                            )}
                                            {activity.start_date && (
                                                <Typography variant="caption" color="text.secondary">
                                                    {formatDate(activity.start_date)}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Box sx={{ textAlign: 'right' }}>
                                            <Typography variant="body1" sx={{ fontWeight: 700, color: '#FF6B35' }}>
                                                ${Number(activity.estimated_cost || 0).toFixed(0)}
                                            </Typography>
                                            {passengerCount > 1 && (
                                                <Typography variant="caption" color="text.secondary">
                                                    per person
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </Paper>
                            ))}
                        </CardContent>
                    </Card>
                )}

                {/* ─── ORDER SUMMARY (Collapsible) ─── */}
                {selectedItems.length > 0 && (
                    <Card sx={{ borderRadius: 3, mb: 3, bgcolor: '#FAFAFA', border: '1px solid #e0e0e0' }}>
                        <CardContent sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    {selectedItems.length} items · {passengerCount} traveler{passengerCount !== 1 ? 's' : ''}
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: '#FF6B35' }}>
                                    ${(totalEstimated * passengerCount).toFixed(2)}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                )}

                {/* No items warning */}
                {tripItems.length === 0 && (
                    <Alert severity="info" sx={{ borderRadius: 2, mb: 3 }}>
                        <Typography variant="body2">
                            No items found for this trip. Go back to the trip details to add flights, hotels, or activities.
                        </Typography>
                    </Alert>
                )}
            </Box>
        </Fade>
    );

    // ═══════════════════════════════════════════════════
    // STEP 2: TRAVELER DETAILS
    // ═══════════════════════════════════════════════════

    const renderStep2 = () => (
        <Fade in={activeStep === 1}>
            <Box>
                {/* Traveler count header */}
                <Alert severity="info" sx={{ borderRadius: 2, mb: 3 }} icon={<People />}>
                    <Typography variant="body2">
                        <strong>Enter details for {passengerCount} traveler{passengerCount !== 1 ? 's' : ''}.</strong>
                        {' '}Names must match government-issued ID exactly.
                    </Typography>
                </Alert>

                <Card sx={{ borderRadius: 3, mb: 3, overflow: 'hidden' }}>
                    <Box sx={{ height: 4, bgcolor: '#FF6B35' }} />
                    <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: 'Poppins, sans-serif', mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Person sx={{ color: '#FF6B35' }} /> Traveler Information
                        </Typography>

                        <TravelerDetailsStep
                            travelers={travelers}
                            onTravelersChange={setTravelers}
                            userProfile={userProfile}
                            hasFlights={hasFlights}
                            hasInternationalFlights={hasInternationalFlights}
                            hasHotels={hotelItems.length > 0}
                            passengerCount={passengerCount}
                        />
                    </CardContent>
                </Card>

                {/* Order Summary (collapsed view) */}
                <Card sx={{ borderRadius: 3, mb: 3, bgcolor: '#FAFAFA' }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    {selectedItems.length} items selected
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {passengerCount} traveler{passengerCount !== 1 ? 's' : ''}
                                </Typography>
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#FF6B35' }}>
                                ${(totalEstimated * passengerCount).toFixed(2)}
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>

                {/* Validation warning */}
                {!canProceedFromStep2() && travelers.length > 0 && (
                    <Alert severity="warning" sx={{ borderRadius: 2 }}>
                        <Typography variant="body2">
                            Please complete all required fields for {passengerCount > travelers.length ? 'all travelers' : 'each traveler'} to continue.
                            {travelers.length < passengerCount && ` (${travelers.length} of ${passengerCount} travelers added)`}
                        </Typography>
                    </Alert>
                )}
            </Box>
        </Fade>
    );

    // ═══════════════════════════════════════════════════
    // STEP 3 (Group Only): COST SPLIT
    // ═══════════════════════════════════════════════════

    const renderCostSplitStep = () => (
        <Fade in={activeStep === 2 && isGroupTrip}>
            <Box>
                <Card sx={{ borderRadius: 3, mb: 3, overflow: 'hidden' }}>
                    <Box sx={{ height: 4, bgcolor: '#2196F3' }} />
                    <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: 'Poppins, sans-serif', mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Group sx={{ color: '#2196F3' }} /> Cost Split
                        </Typography>

                        <Alert severity="info" sx={{ borderRadius: 2, mb: 3 }}>
                            <Typography variant="body2">
                                <strong>Group Trip Cost Sharing</strong><br />
                                Cost sharing model: <strong>{trip.cost_sharing_model || 'Split Equally'}</strong>
                            </Typography>
                        </Alert>

                        {/* Cost breakdown per traveler */}
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#666' }}>
                            Cost per Traveler
                        </Typography>

                        {travelers.map((traveler, index) => (
                            <Paper key={traveler.id} sx={{ p: 2, mb: 1.5, borderRadius: 2, border: '1px solid #e0e0e0' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Avatar sx={{ bgcolor: traveler.isPrimary ? '#FFF3E0' : '#F5F5F5', color: traveler.isPrimary ? '#E65100' : '#666', width: 32, height: 32 }}>
                                            <Person sx={{ fontSize: 18 }} />
                                        </Avatar>
                                        <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {traveler.firstName} {traveler.lastName}
                                                {traveler.isPrimary && (
                                                    <Chip label="You" size="small" sx={{ ml: 1, bgcolor: '#FFF3E0', color: '#E65100', fontWeight: 600, height: 18, fontSize: '0.65rem' }} />
                                                )}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">{traveler.email}</Typography>
                                        </Box>
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#FF6B35' }}>
                                        ${totalEstimated.toFixed(2)}
                                    </Typography>
                                </Box>
                            </Paper>
                        ))}

                        <Divider sx={{ my: 3 }} />

                        {/* Group total */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>Group Total</Typography>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: '#FF6B35' }}>
                                ${(totalEstimated * travelers.length).toFixed(2)}
                            </Typography>
                        </Box>

                        <Alert severity="warning" sx={{ mt: 3, borderRadius: 2 }}>
                            <Typography variant="body2">
                                <strong>Payment Collection:</strong> In this preview, you (the organizer) will pay the full amount.
                                Group payment collection via Stripe Connect coming soon!
                            </Typography>
                        </Alert>
                    </CardContent>
                </Card>
            </Box>
        </Fade>
    );

    // ═══════════════════════════════════════════════════
    // STEP 3 (Individual) / STEP 4 (Group): PAYMENT
    // ═══════════════════════════════════════════════════

    const renderPaymentStep = () => (
        <Fade in={(isGroupTrip && activeStep === 3) || (!isGroupTrip && activeStep === 2)}>
            <Box>
                {/* Order Summary */}
                <Card sx={{ borderRadius: 3, mb: 3, overflow: 'hidden' }}>
                    <Box sx={{ height: 4, bgcolor: '#FF6B35' }} />
                    <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: 'Poppins, sans-serif', mb: 3 }}>
                            Order Summary
                        </Typography>

                        {selectedItems.map(item => (
                            <Box key={item.item_id}>
                                <Box 
                                    sx={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between', 
                                        py: 1.5, 
                                        borderBottom: '1px solid #f0f0f0',
                                        cursor: 'pointer',
                                        '&:hover': { bgcolor: '#FAFAFA' },
                                        borderRadius: 1,
                                        px: 1,
                                        mx: -1,
                                    }}
                                    onClick={() => setExpandedOrderItem(expandedOrderItem === item.item_id ? null : item.item_id)}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Avatar sx={{ bgcolor: `${getItemColor(item.item_type)}15`, color: getItemColor(item.item_type), width: 32, height: 32 }}>
                                            {getItemIcon(item.item_type)}
                                        </Avatar>
                                        <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.title}</Typography>
                                            {item.start_date && (
                                                <Typography variant="caption" color="text.secondary">{formatDate(item.start_date)}</Typography>
                                            )}
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            ${Number(item.estimated_cost || 0).toFixed(2)}
                                            {passengerCount > 1 && item.item_type !== 'hotel' && item.item_type !== 'car' && (
                                                <Typography component="span" variant="caption" color="text.secondary"> × {passengerCount}</Typography>
                                            )}
                                        </Typography>
                                        <ExpandMore sx={{ 
                                            fontSize: 18, 
                                            color: 'text.secondary',
                                            transform: expandedOrderItem === item.item_id ? 'rotate(180deg)' : 'rotate(0deg)',
                                            transition: 'transform 0.2s'
                                        }} />
                                    </Box>
                                </Box>
                                {/* Expanded Details */}
                                {expandedOrderItem === item.item_id && (
                                    <Box sx={{ pl: 6, pr: 2, py: 1.5, bgcolor: '#FAFAFA', borderRadius: 1, mb: 1 }}>
                                        {item.location && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                📍 {item.location}
                                            </Typography>
                                        )}
                                        {item.start_date && item.end_date && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                📅 {formatDate(item.start_date)} - {formatDate(item.end_date)}
                                            </Typography>
                                        )}
                                        {item.notes && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                📝 {item.notes}
                                            </Typography>
                                        )}
                                        {item.item_type === 'flight' && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                ✈️ {passengerCount} passenger{passengerCount > 1 ? 's' : ''}
                                            </Typography>
                                        )}
                                        {item.item_type === 'hotel' && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                🛏️ {tripDays} night{tripDays > 1 ? 's' : ''}
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        ))}

                        {/* Traveler names */}
                        {travelers.length > 0 && (
                            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #eee' }}>
                                <Typography variant="caption" color="text.secondary">Travelers ({travelers.length}):</Typography>
                                <Typography variant="body2">
                                    {travelers.map(t => `${t.firstName} ${t.lastName}`).join(', ')}
                                </Typography>
                            </Box>
                        )}

                        {/* Totals */}
                        <Box sx={{ mt: 3, pt: 2, borderTop: '2px solid #eee' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2" color="text.secondary">Subtotal</Typography>
                                <Typography variant="body2">${(totalEstimated * passengerCount).toFixed(2)}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2" color="text.secondary">Taxes & Fees (~12%)</Typography>
                                <Typography variant="body2">${(totalEstimated * passengerCount * 0.12).toFixed(2)}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2" sx={{ color: '#2D6E6A', fontWeight: 600 }}>💎 Percoro Credits</Typography>
                                <Typography variant="body2" sx={{ color: '#2D6E6A' }}>-$0.00</Typography>
                            </Box>
                            <Divider sx={{ my: 1.5 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>Total</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: '#FF6B35' }}>
                                    ${((totalEstimated * passengerCount) * 1.12).toFixed(2)}
                                </Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>

                {/* Payment Card */}
                <Card sx={{ borderRadius: 3, mb: 3 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: 'Poppins, sans-serif', mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CreditCard sx={{ color: '#FF6B35' }} /> Payment
                        </Typography>

                        <Alert severity="info" sx={{ borderRadius: 2, mb: 3 }} icon={<Lock />}>
                            <Typography variant="body2">
                                <strong>Stripe Integration Coming Soon.</strong> Your payment will be processed securely via Stripe.
                                No credit card information is stored on our servers.
                            </Typography>
                        </Alert>

                        {/* CC form placeholder */}
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField fullWidth label="Card Number" placeholder="1234 5678 9012 3456" disabled
                                    InputProps={{ startAdornment: <CreditCard sx={{ mr: 1, color: '#ccc' }} /> }}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField fullWidth label="Expiry" placeholder="MM / YY" disabled
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField fullWidth label="CVC" placeholder="123" disabled
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField fullWidth label="Name on Card" placeholder="Full name" disabled
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                            </Grid>
                        </Grid>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, color: 'text.secondary' }}>
                            <Shield sx={{ fontSize: 16 }} />
                            <Typography variant="caption">256-bit SSL encrypted · PCI DSS compliant</Typography>
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        </Fade>
    );

    // ═══════════════════════════════════════════════════
    // STEP 4 (Individual) / STEP 5 (Group): CONFIRMATION
    // ═══════════════════════════════════════════════════

    const renderConfirmationStep = () => (
        <Fade in={(isGroupTrip && activeStep === 4) || (!isGroupTrip && activeStep === 3)}>
            <Box>
                <Card sx={{ borderRadius: 3, overflow: 'hidden', textAlign: 'center' }}>
                    <Box sx={{ height: 4, bgcolor: '#4CAF50' }} />
                    <CardContent sx={{ p: 5 }}>
                        <Celebration sx={{ fontSize: 64, color: '#FF6B35', mb: 2 }} />
                        <Typography variant="h4" sx={{ fontWeight: 700, fontFamily: 'Poppins, sans-serif', mb: 1 }}>
                            Booking Confirmed!
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                            Your {isGroupTrip ? 'group ' : ''}trip to <strong>{trip.destination}</strong> is booked. 🎉
                        </Typography>

                        {confirmationCode && (
                            <Paper sx={{ p: 2, mb: 3, bgcolor: '#F5F5F5', borderRadius: 2, display: 'inline-block' }}>
                                <Typography variant="caption" color="text.secondary">Confirmation Code</Typography>
                                <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: 'monospace', color: '#2D6E6A' }}>
                                    {confirmationCode}
                                </Typography>
                            </Paper>
                        )}

                        {/* What's booked */}
                        <Box sx={{ textAlign: 'left', mb: 3, p: 2, bgcolor: '#F5F5F5', borderRadius: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>What's Booked:</Typography>
                            {selectedItems.map(item => (
                                <Box key={item.item_id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                                    <CheckCircle sx={{ color: '#4CAF50', fontSize: 18 }} />
                                    <Typography variant="body2">{item.title}</Typography>
                                </Box>
                            ))}
                            {travelers.length > 0 && (
                                <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #ddd' }}>
                                    <Typography variant="caption" color="text.secondary">Travelers ({travelers.length}):</Typography>
                                    {travelers.map(t => (
                                        <Typography key={t.id} variant="body2">{t.firstName} {t.lastName}</Typography>
                                    ))}
                                </Box>
                            )}
                        </Box>

                        <Alert severity="info" sx={{ borderRadius: 2, mb: 3, textAlign: 'left' }}>
                            <Typography variant="body2">
                                This is a preview of the confirmation page. Once Stripe and booking APIs are integrated,
                                real bookings will generate confirmation codes and email receipts.
                            </Typography>
                        </Alert>

                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                            <Button variant="contained" onClick={() => navigate(`/trips/${tripId}`)}
                                sx={{ bgcolor: '#2D6E6A', borderRadius: '24px', textTransform: 'none', fontWeight: 600, px: 4, '&:hover': { bgcolor: '#009688' } }}>
                                View Trip
                            </Button>
                            <Button variant="outlined" onClick={() => navigate('/trips')}
                                sx={{ borderColor: '#2D6E6A', color: '#2D6E6A', borderRadius: '24px', textTransform: 'none', fontWeight: 600, px: 4 }}>
                                My Trips
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        </Fade>
    );

    // ═══════════════════════════════════════════════════
    // MAIN RENDER
    // ═══════════════════════════════════════════════════

    // Determine which step content to show
    const isConfirmationStep = (isGroupTrip && activeStep === 4) || (!isGroupTrip && activeStep === 3);
    const isPaymentStep = (isGroupTrip && activeStep === 3) || (!isGroupTrip && activeStep === 2);
    const isCostSplitStep = isGroupTrip && activeStep === 2;

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#FAFAFA' }}>
            {/* Top Bar */}
            <AppBar position="sticky" color="transparent" elevation={0}
                sx={{ borderBottom: '1px solid #eee', bgcolor: 'white', zIndex: 10 }}>
                <Toolbar>
                    <IconButton onClick={() => activeStep === 0 ? navigate(`/trips/${tripId}`) : setActiveStep(prev => prev - 1)} sx={{ mr: 2 }}>
                        <ArrowBack />
                    </IconButton>
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: 'Poppins, sans-serif', display: 'flex', alignItems: 'center', gap: 1 }}>
                            Book Your {isGroupTrip ? 'Group ' : ''}Trip
                            {isGroupTrip && <Group sx={{ fontSize: 20, color: '#2196F3' }} />}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {trip.title} · {trip.destination}
                        </Typography>
                    </Box>
                    {!isConfirmationStep && (
                        <Typography variant="body2" sx={{ color: '#FF6B35', fontWeight: 600 }}>
                            Step {activeStep + 1} of {STEPS.length}
                        </Typography>
                    )}
                </Toolbar>
            </AppBar>

            {/* Stepper */}
            {!isConfirmationStep && (
                <Box sx={{ bgcolor: 'white', borderBottom: '1px solid #eee', py: 2 }}>
                    <Container maxWidth="lg">
                        <Stepper activeStep={activeStep} alternativeLabel>
                            {STEPS.map((step, idx) => (
                                <Step key={step.label} completed={idx < activeStep}>
                                    <StepLabel
                                        StepIconProps={{
                                            sx: {
                                                '&.Mui-active': { color: '#FF6B35' },
                                                '&.Mui-completed': { color: '#2D6E6A' },
                                            }
                                        }}
                                    >
                                        <Typography variant="caption" sx={{
                                            fontWeight: idx === activeStep ? 700 : 400,
                                            color: idx === activeStep ? '#FF6B35' : idx < activeStep ? '#2D6E6A' : '#999',
                                        }}>
                                            {step.label}
                                        </Typography>
                                    </StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    </Container>
                </Box>
            )}

            {/* Content */}
            <Container maxWidth="md" sx={{ py: 4 }}>
                {activeStep === 0 && renderStep1()}
                {activeStep === 1 && renderStep2()}
                {isCostSplitStep && renderCostSplitStep()}
                {isPaymentStep && renderPaymentStep()}
                {isConfirmationStep && renderConfirmationStep()}

                {/* Navigation Buttons */}
                {!isConfirmationStep && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                        <Button
                            variant="outlined"
                            startIcon={<ArrowBackIos sx={{ fontSize: 14 }} />}
                            onClick={() => activeStep === 0 ? navigate(`/trips/${tripId}`) : setActiveStep(prev => prev - 1)}
                            sx={{ borderRadius: '24px', textTransform: 'none', borderColor: '#ccc', color: '#666', px: 3 }}
                        >
                            {activeStep === 0 ? 'Back to Trip' : 'Back'}
                        </Button>

                        {activeStep === 0 && (
                            <Button
                                variant="contained"
                                endIcon={savingSelections ? <CircularProgress size={20} color="inherit" /> : <ArrowForward />}
                                onClick={handleSaveSelectionsAndProceed}
                                disabled={!canProceedFromStep1() || savingSelections}
                                sx={{
                                    bgcolor: '#FF6B35', borderRadius: '24px', textTransform: 'none',
                                    fontWeight: 700, px: 4, fontFamily: 'Poppins, sans-serif',
                                    '&:hover': { bgcolor: '#E55A2B' },
                                    '&.Mui-disabled': { bgcolor: '#ffccbc', color: 'white' },
                                }}
                            >
                                {savingSelections ? 'Saving...' : 'Continue to Traveler Details'}
                            </Button>
                        )}

                        {activeStep === 1 && (
                            <Button
                                variant="contained"
                                endIcon={<ArrowForward />}
                                onClick={handleProceedToPayment}
                                disabled={!canProceedFromStep2()}
                                sx={{
                                    bgcolor: '#FF6B35', borderRadius: '24px', textTransform: 'none',
                                    fontWeight: 700, px: 4, fontFamily: 'Poppins, sans-serif',
                                    '&:hover': { bgcolor: '#E55A2B' },
                                    '&.Mui-disabled': { bgcolor: '#ffccbc', color: 'white' },
                                }}
                            >
                                Continue to {isGroupTrip ? 'Cost Split' : 'Payment'}
                            </Button>
                        )}

                        {isCostSplitStep && (
                            <Button
                                variant="contained"
                                endIcon={<ArrowForward />}
                                onClick={handleProceedFromCostSplit}
                                sx={{
                                    bgcolor: '#FF6B35', borderRadius: '24px', textTransform: 'none',
                                    fontWeight: 700, px: 4, fontFamily: 'Poppins, sans-serif',
                                    '&:hover': { bgcolor: '#E55A2B' },
                                }}
                            >
                                Continue to Payment
                            </Button>
                        )}

                        {isPaymentStep && (
                            <Button
                                variant="contained"
                                startIcon={paymentProcessing ? <CircularProgress size={20} color="inherit" /> : <Lock />}
                                disabled={paymentProcessing}
                                onClick={handleCompleteBooking}
                                sx={{
                                    bgcolor: '#FF6B35', borderRadius: '24px', textTransform: 'none',
                                    fontWeight: 700, px: 4, py: 1.25, fontSize: '1rem',
                                    fontFamily: 'Poppins, sans-serif',
                                    boxShadow: '0 3px 12px rgba(255,107,53,0.35)',
                                    '&:hover': { bgcolor: '#E55A2B' },
                                }}
                            >
                                {paymentProcessing ? 'Processing...' : 'Complete Booking'}
                            </Button>
                        )}
                    </Box>
                )}
            </Container>

            {/* Add Hotel Prompt Dialog */}
            <Dialog open={showAddHotelPrompt} onClose={() => setShowAddHotelPrompt(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Hotel sx={{ color: '#4A6FA5' }} /> Add a Hotel?
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        You haven't added any hotels to this trip yet. Would you like to search for hotels now?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2.5 }}>
                    <Button onClick={() => setShowAddHotelPrompt(false)} sx={{ textTransform: 'none' }}>
                        Skip for Now
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => { setShowAddHotelPrompt(false); setHotelSearchOpen(true); }}
                        sx={{ bgcolor: '#4A6FA5', textTransform: 'none', borderRadius: 2, '&:hover': { bgcolor: '#3D5A8A' } }}
                    >
                        Search Hotels
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add Car Prompt Dialog */}
            <Dialog open={showAddCarPrompt} onClose={() => setShowAddCarPrompt(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DirectionsCar sx={{ color: '#607D8B' }} /> Add a Rental Car?
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        Would you like to add a rental car to your trip?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2.5 }}>
                    <Button onClick={() => setShowAddCarPrompt(false)} sx={{ textTransform: 'none' }}>
                        No Thanks
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => { setShowAddCarPrompt(false); setCarSearchOpen(true); }}
                        sx={{ bgcolor: '#607D8B', textTransform: 'none', borderRadius: 2, '&:hover': { bgcolor: '#455A64' } }}
                    >
                        Search Cars
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Price Change Dialog */}
            <Dialog 
                open={showPriceChangeDialog} 
                onClose={() => setShowPriceChangeDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 700, fontFamily: 'Poppins, sans-serif' }}>
                    ⚠️ Price Changes Detected
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Some prices have changed since you added these items. Review the changes below:
                    </Typography>
                    
                    {Object.entries(priceChanges).map(([itemId, change]) => {
                        const item = tripItems.find(i => i.item_id === itemId);
                        if (!item) return null;
                        
                        return (
                            <Card key={itemId} variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
                                <CardContent sx={{ py: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Avatar sx={{ 
                                                bgcolor: change.available ? `${getItemColor(item.item_type)}15` : '#ffebee',
                                                color: change.available ? getItemColor(item.item_type) : '#f44336',
                                                width: 36, 
                                                height: 36 
                                            }}>
                                                {getItemIcon(item.item_type)}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {item.title}
                                                </Typography>
                                                {!change.available ? (
                                                    <Chip 
                                                        label="No longer available" 
                                                        size="small" 
                                                        sx={{ bgcolor: '#ffebee', color: '#c62828', mt: 0.5 }} 
                                                    />
                                                ) : (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                                        <Typography 
                                                            variant="caption" 
                                                            sx={{ textDecoration: 'line-through', color: 'text.secondary' }}
                                                        >
                                                            ${change.oldPrice.toFixed(2)}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ color: '#f44336', fontWeight: 600 }}>
                                                            → ${change.newPrice.toFixed(2)}
                                                        </Typography>
                                                        <Chip 
                                                            label={`+$${(change.newPrice - change.oldPrice).toFixed(0)}`}
                                                            size="small"
                                                            sx={{ bgcolor: '#fff3e0', color: '#e65100', height: 20, fontSize: '0.7rem' }}
                                                        />
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => handleFindAlternative(item.item_type)}
                                            sx={{ 
                                                borderRadius: 2, 
                                                textTransform: 'none',
                                                borderColor: '#2D6E6A',
                                                color: '#2D6E6A',
                                            }}
                                        >
                                            Find Alternative
                                        </Button>
                                    </Box>
                                </CardContent>
                            </Card>
                        );
                    })}
                </DialogContent>
                <DialogActions sx={{ p: 2.5, gap: 1 }}>
                    <Button 
                        onClick={() => setShowPriceChangeDialog(false)}
                        sx={{ textTransform: 'none' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleAcceptPriceChanges}
                        disabled={Object.values(priceChanges).every(c => !c.available)}
                        sx={{ 
                            bgcolor: '#2D6E6A', 
                            textTransform: 'none',
                            borderRadius: 2,
                            '&:hover': { bgcolor: '#009688' }
                        }}
                    >
                        Accept New Prices
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Hotel Search Dialog */}
            <HotelSearchDialog
                open={hotelSearchOpen}
                onClose={() => setHotelSearchOpen(false)}
                tripId={tripId || ''}
                destination={trip?.destination || ''}
                checkIn={trip?.startDate || ''}
                checkOut={trip?.endDate || ''}
                guests={passengerCount}
                onHotelsSelected={handleHotelsSelected}
            />

            {/* Car Search Dialog */}
            <CarSearchDialog
                open={carSearchOpen}
                onClose={() => setCarSearchOpen(false)}
                tripId={tripId || ''}
                pickupLocation={trip?.destination || ''}
                pickupDate={trip?.startDate || ''}
                dropoffDate={trip?.endDate || ''}
                onCarsSelected={handleCarsSelected}
            />

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                message={snackbar.message}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </Box>
    );
};

export default BookingCheckoutPage;

